import type { components, operations } from '@luxuryescapes/contract-svc-audit-v2/dist/server';

import { AuditLog, AuditLogResponse } from '~/types/responses';

import { json_headers, request } from './common';

const BASE_URL = '/api/audit/v2/events/fetch';

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_TIME_FRAME_MONTHS = 24;

function getBasePath() {
  return window.configs.API_HOST;
}

// Re-export types from contract for external use
export type AuditEvent = components['schemas']['AuditEvent'];
export type StringFilter = components['schemas']['StringFilter'];
export type StringFilterWithoutIn = components['schemas']['StringFilterWithoutIn'];
export type DateFilter = components['schemas']['DateFilter'];
export type StringFilterMatch = keyof Exclude<StringFilter, string>;
export type ResourceMatch = Extract<StringFilterMatch, 'equals' | 'starts_with' | 'ends_with' | 'contains'>;

// "equals" and "starts_with" can use the resource index, the others require a full scan
export const DEFAULT_RESOURCE_MATCH: ResourceMatch = 'equals';
export const RESOURCE_MATCH_OPTIONS: Array<{ value: ResourceMatch; label: string }> = [
  { value: 'equals', label: 'equals' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with (slow)' },
  { value: 'contains', label: 'contains (slow)' },
];

// Request body type from the contract
export type SearchAuditEventsRequest = operations['searchAuditEvents']['requestBody']['content']['application/json'];

/**
 * Calculate ISO date string for a given number of months ago
 */
export function getTimestampFilterFromMonths(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString();
}

// Mapped audit log for grouping operations
export type MappedAuditLog = {
  logId: string;
  action: string;
  timestamp: string;
  userEmail: string;
  modelId: string;
  meta: AuditEvent['meta'];
  diff?: NonNullable<AuditEvent['model']>['diff'];
};

/**
 * Convert page/limit to skip/take
 */
function pageToSkipTake(page: number, limit: number): { skip: number; take: number } {
  return {
    skip: (page - 1) * limit,
    take: limit,
  };
}

/**
 * Search audit events with filters (POST request to v2 API)
 */
export async function searchAuditEvents(
  searchRequest: SearchAuditEventsRequest,
  options?: { signal?: AbortSignal },
): Promise<AuditLogResponse> {
  return request(`${getBasePath()}${BASE_URL}`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(searchRequest),
    signal: options?.signal,
  }) as Promise<AuditLogResponse>;
}

/**
 * Get logs filtered by resource using the given match type
 * Be aware that 'contains' and 'ends_with' are much slower than 'equals' and 'starts_with'
 */
export async function getLogsByResource(
  resource: string,
  match: StringFilterMatch,
  page = 1,
  limit = DEFAULT_PAGE_SIZE,
  totalCount = false,
  timeFrameMonths: number = DEFAULT_TIME_FRAME_MONTHS,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      resource: { [match]: resource },
      timestamp: { gte: getTimestampFilterFromMonths(timeFrameMonths) },
    },
  });
}

/**
 * Get logs filtered by actor email
 */
export async function getLogsByActorEmail(
  email: string,
  page = 1,
  limit = DEFAULT_PAGE_SIZE,
  totalCount = false,
  timeFrameMonths: number = DEFAULT_TIME_FRAME_MONTHS,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      actor: { email },
      timestamp: { gte: getTimestampFilterFromMonths(timeFrameMonths) },
    },
  });
}

/**
 * Get logs filtered by model type and ID
 */
export async function getLogsByModelAndId(
  modelType: string,
  modelId: string,
  page = 1,
  limit = DEFAULT_PAGE_SIZE,
  totalCount = false,
  timeFrameMonths: number = DEFAULT_TIME_FRAME_MONTHS,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      model: {
        type: modelType,
        id: modelId,
      },
      timestamp: { gte: getTimestampFilterFromMonths(timeFrameMonths) },
    },
  });
}

/**
 * Get logs filtered by service, model type, and model ID
 */
export async function getLogsByServiceModelAndId(
  serviceName: string,
  modelType: string,
  modelId: string,
  page = 1,
  limit = DEFAULT_PAGE_SIZE,
  totalCount = false,
  timeFrameMonths: number = DEFAULT_TIME_FRAME_MONTHS,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      service: serviceName,
      model: {
        type: modelType,
        id: modelId,
      },
      timestamp: { gte: getTimestampFilterFromMonths(timeFrameMonths) },
    },
  });
}

/**
 * Get logs filtered by model type only
 */
export async function getLogsByModel(
  modelType: string,
  page = 1,
  limit = DEFAULT_PAGE_SIZE,
  totalCount = false,
  timeFrameMonths: number = DEFAULT_TIME_FRAME_MONTHS,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      model: { type: modelType },
      timestamp: { gte: getTimestampFilterFromMonths(timeFrameMonths) },
    },
  });
}

/**
 * Get logs filtered by meta key and value
 */
export async function getLogsByMetaKeyValue(
  key: string,
  value: string,
  page = 1,
  limit = DEFAULT_PAGE_SIZE,
  totalCount = false,
  timeFrameMonths: number = DEFAULT_TIME_FRAME_MONTHS,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      meta: {
        [key]: value,
      },
      timestamp: { gte: getTimestampFilterFromMonths(timeFrameMonths) },
    },
  });
}

/**
 * Get logs filtered by service name
 */
export async function getLogsByService(
  serviceName: string,
  page = 1,
  limit = DEFAULT_PAGE_SIZE,
  totalCount = false,
  timeFrameMonths: number = DEFAULT_TIME_FRAME_MONTHS,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      service: serviceName,
      timestamp: { gte: getTimestampFilterFromMonths(timeFrameMonths) },
    },
  });
}

/**
 * Group audit logs by model ID for display purposes
 */
export function groupLogsByModelId(
  logs: Array<AuditLog>,
  getIdForDelete: (log: AuditLog) => string,
): { [key: string]: Array<MappedAuditLog> } {
  return logs
    .flatMap((log) => {
      const actorEmail = log.actor.email ?? '';

      if (log.action === 'Delete') {
        return {
          logId: log.id,
          action: log.action,
          timestamp: log.timestamp,
          userEmail: actorEmail,
          meta: log.meta,
          modelId: getIdForDelete(log),
        };
      }

      if (!log.model) {
        return [];
      }

      return {
        logId: log.id,
        action: log.action,
        timestamp: log.timestamp,
        userEmail: actorEmail,
        meta: log.meta,
        modelId: log.model.id,
        diff: log.model.diff,
      };
    })
    .reduce(
      (logMap, log) => {
        if (logMap[log.modelId]) {
          logMap[log.modelId].push(log);
        } else {
          logMap[log.modelId] = [log];
        }

        return logMap;
      },
      {} as { [key: string]: Array<MappedAuditLog> },
    );
}

/**
 * Group audit logs by model type for display purposes
 */
export function groupLogsByModelType(logs: Array<AuditLog>): { [key: string]: Array<AuditLog> } {
  const map: { [key: string]: Array<AuditLog> } = {};

  logs.forEach((log) => {
    if (log.model) {
      if (!map[log.model.type]) {
        map[log.model.type] = [];
      }
      map[log.model.type].push(log);
    }
  });

  return map;
}
